// src/components/Paavalikko/Paavalikko.jsx
import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import SocialLinks from "../Social/SocialLinks.jsx";
import {
  Menu, X, LogIn, Phone, Sun, Spotlight, ChevronDown,
  UserPlus, HelpCircle, LogOut, LayoutDashboard
} from "lucide-react";
import SearchBar from "../SearchBar/SearchBar.jsx";
import { useTheme } from "../../context/ThemeContext";
import { useAuth } from "../../context/AuthContext";
import NeonButton from "../ResponsiveButton/NeonButton.jsx";
import ConfettiLinkLabel from "../Nav/ConfettiLinkLabel";
import DiscoScrollTop from "../ScrollTop/DiscoScrollTop";
import { DASH_USER_QS } from "../../utils/nav";
import LogoLight from "../../assets/logo-buukkeri-dark.webp";
import LogoDark from "../../assets/logo-buukkeri-dark.webp";

/** Pieni luokkien yhdistin */
const cx = (...c) => c.filter(Boolean).join(" ");

/** Päänavigaation peruslinkit (desktop & mobiili) */
const navItems = [
  { to: "/", label: "Etusivu" },
  { to: "/blogi", label: "Blogi" },
  { to: "/tietoameista", label: "Tietoa meistä" },
  { to: "/useinkysyttya", label: "UKK" },
];

/** Mobiilissa näytettävät emojit (palvelu-osiolle) */
const MOBILE_EMOJI = {
  "/": "🏠",
  "/blogi": "📰",
  "/tietoameista": "ℹ️",
  "/useinkysyttya": "❓",
};

export default function SiteHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === "dark";

  // ---------------------------
  // Auth-tila (memoized)
  // ---------------------------
  const rawAuth = useAuth?.();
  const auth = useMemo(() => rawAuth || {}, [rawAuth]);

  // Salli erilaiset user-rakenteet: user, currentUser, id/_id/user_id/email
  const { user: authUser, currentUser: authCurrentUser, token: authToken, isAuthenticated } = auth;
  const userLike = authUser ?? authCurrentUser ?? null;
  const hasUserId = !!(userLike && (userLike.id || userLike._id || userLike.user_id || userLike.email));

  // Contextin "raakafakta"
  const resolvedIsAuthed = Boolean(
    typeof isAuthenticated === "boolean" ? isAuthenticated : (authToken || hasUserId)
  );

  // Paavalikon oma peilaus, joka pakottaa rerenderin heti
  const [isAuthed, setIsAuthed] = useState(resolvedIsAuthed);

  useEffect(() => {
    setIsAuthed(resolvedIsAuthed);
  }, [resolvedIsAuthed]);


  // Normalisoitu polku ("/" tai ilman trailing-slashia)
  const norm = (p) => (p === "/" ? "/" : p.replace(/\/+$/, ""));
  const path = norm(location.pathname);

  // --- Onboarding-flow'n tunnistus ---
  const isTermsLike = ["/kayttoehdot", "/ohjeet-ja-ehdot", "/tietosuojaseloste"].includes(path);

  const cameFromOnboarding = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    if ((sp.get("from") || "").toLowerCase() === "onboarding") return true;
    if (location.state?.fromOnboarding === true) return true;
    if (typeof document !== "undefined" && /\/onboarding(\b|\/|$)/.test(document.referrer || "")) {
      return true;
    }
    return false;
  }, [location.search, location.state]);

  const isOnboardingFlow = useMemo(() => {
    const roots = ["/rekisteroidy", "/verify-email", "/onboarding", "/setup"];
    if (roots.some((p) => path.startsWith(p))) return true;
    if (isTermsLike && cameFromOnboarding) return true;
    return false;
  }, [path, isTermsLike, cameFromOnboarding]);

  // Näytetään "Kirjaudu ulos" vain kun ei olla onboardingissa
  const canShowLogout = isAuthed && !isOnboardingFlow;

  // Näytetään "Hallintapaneeli" vain jos kirjautunut EIKÄ olla onboarding-virrassa
  const canShowDashboard = isAuthed && !isOnboardingFlow;

  // Rekisteröitymis-/kirjautumissivut?
  const isRegisterPage = path === "/rekisteroidy";
  const isLoginPage = path === "/kirjaudu";

  // Näytetään "Ota yhteyttä" –CTA kaikkialla paitsi yhteydenottosivulla
  const showContactCTA = path !== "/otayhteytta";

  // Headerin UI-tilat
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hideHeader, setHideHeader] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const lastY = useRef(0);

  // Esiintyjille-dropdown (desktop)
  const [artistsMenuOpen, setArtistsMenuOpen] = useState(false);
  const artistsMenuRef = useRef(null);
  const artistsButtonRef = useRef(null);

  /** Scroll-käyttäytyminen: pieni autohide ja “Ylös”-nappi kun > 300px */
  useEffect(() => {
    lastY.current = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 8);
      const delta = Math.abs(y - lastY.current);
      if (delta > 6) {
        const goingDown = y > lastY.current;
        if (goingDown && y > 80) {
          setHideHeader(true);
          setShowScrollTop(y > 300);
        } else {
          setHideHeader(false);
          if (y < 300) setShowScrollTop(false);
        }
        lastY.current = y;
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /** Body-scrollin lukitus mobiilipaneelin ollessa auki */
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => (document.body.style.overflow = prev);
  }, [open]);

  /** Esc sulkee paneelit */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        setArtistsMenuOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  /** Klikki ulos → sulje artisti-dropdown */
  useEffect(() => {
    if (!artistsMenuOpen) return;
    const onDocClick = (e) => {
      if (!artistsMenuRef.current) return;
      if (
        !artistsMenuRef.current.contains(e.target) &&
        !artistsButtonRef.current?.contains(e.target)
      ) {
        setArtistsMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [artistsMenuOpen]);

  /* ===========================
     LINKKIEN TYYLIT (DESKTOP)
     =========================== */
  const baseLink =
    "relative px-2 py-2 rounded-md font-extrabold uppercase transition-colors duration-300 " +
    "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2";

  const activeDesk = isDark
    ? (scrolled ? "text-white" : "text-white")
    : (scrolled ? "text-black" : "text-black");

  const inactiveDesk = isDark
    ? (scrolled ? "text-white/80 hover:text-white" : "text-white/40 hover:text-white/90")
    : (scrolled ? "text-black/60 hover:text-black/90" : "text-black/90 hover:text-pink/100");

  /* ===========================
     HEADER-TAUSTAT
     =========================== */
  const headerBgWhenScrolled = isDark
    ? "bg-night-900 text-white border-b border-black/50 shadow-lg shadow-black/70"
    : "bg-lemon/70 text-white border-b border-black/10 shadow-lg shadow-black/50";

  const headerBgTop = cx(
    "transition-shadow duration-300",
    isDark
      ? "backdrop-blur-md bg-night-900/70 text-white border-white/10 shadow-black/70"
      : "backdrop-blur-0 bg-lemon/70 text-black border-black/10 shadow-black/50"
  );

  const headerGradientTop = isDark
    ? "pointer-events-none absolute inset-0 z-10 bg-gradient-to-b from-night-900/90 via-night-900/40 to-transparent"
    : "pointer-events-none absolute inset-0 z-10 bg-gradient-to-b from-lemon/70 via-lemon/30 to-transparent";

  /* ===========================
     MOBIILI: OVERLAY & PANEELI
     =========================== */
  const overlayClass = "bg-black/90";

  // Paneelin tausta
  const panelBg = isDark
    ? "text-white bg-[radial-gradient(1200px_500px_at_110%_-10%,rgba(79,70,229,0.25),transparent_60%),radial-gradient(900px_500px_at_-10%_110%,rgba(236,72,153,0.18),transparent_60%),linear-gradient(180deg,#0a0f1c_0%,#0b1020_45%,#090d19_100%)]"

    : "text-white bg-gradient-to-b from-electric/90 to-black/80";

  /* ===========================
     MOBIILILINKIT
     =========================== */
  const mobileLinkBase =
    "group relative block rounded-xl px-4 py-2 text-xs font-semibold tracking-wide transition";
  const mobileLinkActive = "bg-white/10 text-white ring-1 ring-white/15 shadow-[0_0_24px_rgba(255,255,255,0.12)]";
  const mobileLinkInactive = "text-white/100 hover:text-white hover:bg-white/5";

  /* ===========================
     TEEMAKYTKIN BOX
     =========================== */
  const themeToggleBox = isDark
    ? "border-white/90 bg-white/20 hover:bg-electric/50 focus:ring-2 focus:ring-white/70"
    : "border-pink/90 bg-pink/50 hover:bg-black/30 focus:ring-2 focus:ring-white/80";

  /* ===========================
     LOGOUT-HANDLER
     =========================== */
  const { logout: logoutFn } = auth;
  const loggingOutRef = useRef(false);

  const handleLogout = useCallback(() => {
    if (loggingOutRef.current) return;
    loggingOutRef.current = true;

    try {
      // Käynnistä logout “taustalla”
      if (typeof logoutFn === "function") {
        logoutFn({ redirect: false, hard: true });
      }
    } catch (e) {
      console.warn("logoutFn heitti virheen", e);
    }

    // Päivitä headerin oma state heti → nappi ja dashboard-linkki katoaa
    setIsAuthed(false);

    // UI-reaktio heti:
    setOpen(false);
    setArtistsMenuOpen(false);
    navigate("/");

    // vapauta lippu seuraavaa klikkausta varten
    setTimeout(() => {
      loggingOutRef.current = false;
    }, 0);
  }, [logoutFn, navigate]);



  return (
    <>
      {/* HEADER */}
      <motion.header
        initial={false}
        animate={{ y: hideHeader ? -90 : 0, height: scrolled ? 64 : 80 }}
        transition={{ type: "spring", stiffness: 260, damping: 30 }}
        className="navbar-fixed fixed top-0 left-0 right-0 z-50"
      >
        <div
          className={cx(
            "relative overflow-visible backdrop-blur-md transition-shadow duration-300",
            scrolled ? headerBgWhenScrolled : headerBgTop
          )}
        >
          {!scrolled && <div className={headerGradientTop} aria-hidden />}

          <div className="mx-auto max-w-7xl px-4">
            <div className="flex h-20 items-center justify-between">

              {/* Logo */}
              <NavLink
                to="/"
                className="inline-flex items-center gap-2 py-2 rounded-xl font-customFont2 text-2xl font-extrabold text-black/90 dark:text-white hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                aria-label="Etusivu"
              >
                <img src={LogoLight} alt="" className="h-6 w-auto shrink-0 dark:hidden" />
                <img src={LogoDark}  alt="" className="h-6 w-auto shrink-0 hidden dark:block" />
                <span className="hidden xxsm:inline s24:inline xsm:inline sm:inline md:inline mdd:inline lg:inline xl:inline 2xl:inline">
                  Buukkeri
                </span>
              </NavLink>

              {/* Desktop nav + dropdown */}
              <nav className="hidden lg:flex xl:flex 2xl:flex items-center gap-1">
                {navItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) => cx(baseLink, isActive ? activeDesk : inactiveDesk)}
                  >
                    {({ isActive }) => (
                      <ConfettiLinkLabel isActive={isActive}>{item.label}</ConfettiLinkLabel>
                    )}
                  </NavLink>
                ))}

                {/* Esiintyjille dropdown (desktop) */}
                <div className="relative">
                  <button
                    ref={artistsButtonRef}
                    type="button"
                    className={cx(
                      baseLink,
                      "inline-flex items-center gap-1",
                      location.pathname.startsWith("/liity-esiintyjaksi") ||
                      location.pathname.startsWith("/ukk-esiintyjille") ||
                      location.pathname.startsWith("/backstage")
                        ? activeDesk
                        : inactiveDesk
                    )}
                    aria-haspopup="menu"
                    aria-expanded={artistsMenuOpen}
                    onClick={() => setArtistsMenuOpen((o) => !o)}
                    onMouseEnter={() => setArtistsMenuOpen(true)}
                    onMouseLeave={() => setArtistsMenuOpen(false)}
                  >
                    <span>Esiintyjille</span>
                    <ChevronDown
                      className={cx("h-4 w-4 transition-transform", artistsMenuOpen && "rotate-180")}
                    />
                  </button>

                  <AnimatePresence>
                    {artistsMenuOpen && (
                      <motion.div
                        ref={artistsMenuRef}
                        initial={{ opacity: 0, y: 8, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 8, scale: 0.98 }}
                        transition={{ type: "spring", stiffness: 260, damping: 20 }}
                        onMouseEnter={() => setArtistsMenuOpen(true)}
                        onMouseLeave={() => setArtistsMenuOpen(false)}
                        className={cx(
                          "absolute right-0 mt-2 w-56 rounded-xl backdrop-blur-md shadow-2xl p-1 z-[60] border",
                          isDark ? "border-white/10 bg-black/95 text-white"
                                 : "border-white/10 bg-black/90 text-white"
                        )}
                        role="menu"
                        aria-label="Esiintyjille"
                      >
                        <NavLink
                          to="/ukk-esiintyjille"
                          onClick={() => setArtistsMenuOpen(false)}
                          className={({ isActive }) =>
                            cx(
                              "block rounded-lg px-3 py-2 text-sm",
                              isActive
                                ? "bg-white/10 text-primary"
                                : "hover:bg-white/5"
                            )
                          }
                          role="menuitem"
                        >
                          <span className="inline-flex items-center gap-2">
                            <HelpCircle className="h-4 w-4 shrink-0" aria-hidden />
                            <span>Esiintyjien UKK</span>
                          </span>
                        </NavLink>

                        {canShowDashboard && (
                          <NavLink
                            to={`/dashboard?${DASH_USER_QS}`}
                            onClick={() => setArtistsMenuOpen(false)}
                            className={({ isActive }) =>
                              cx(
                                "block rounded-lg px-3 py-2 text-sm",
                                isActive ? "bg-white/10 text-primary" : "hover:bg-white/5"
                              )
                            }
                            role="menuitem"
                          >
                            <span className="inline-flex items-center gap-2">
                              <LayoutDashboard className="h-4 w-4 shrink-0" aria-hidden />
                              <span>Hallintapaneeli</span>
                            </span>
                          </NavLink>
                        )}

                        {!isAuthed && !isLoginPage && (
                          <NavLink
                            to="/kirjaudu"
                            onClick={() => setArtistsMenuOpen(false)}
                            className={({ isActive }) =>
                              cx(
                                "block rounded-lg px-3 py-2 text-sm",
                                isActive
                                  ? isDark
                                    ? "bg-white/10 text-primary"
                                    : "bg-black/5 text-primary"
                                  : isDark
                                  ? "hover:bg-white/5"
                                  : "hover:bg-black/5"
                              )
                            }
                            role="menuitem"
                          >
                            <span className="inline-flex items-center gap-2">
                              <LogIn className="h-4 w-4 shrink-0" aria-hidden />
                              <span>Kirjaudu sisään</span>
                            </span>
                          </NavLink>
                        )}

                        {!isAuthed && !isRegisterPage && (
                          <NavLink
                            to="/rekisteroidy"
                            onClick={() => setArtistsMenuOpen(false)}
                            className={({ isActive }) =>
                              cx(
                                "block rounded-lg px-3 py-2 text-sm",
                                isActive ? "bg-white/10 text-primary" : "hover:bg-white/5"
                              )
                            }
                            role="menuitem"
                          >
                            <span className="inline-flex items-center gap-2">
                              <UserPlus className="h-4 w-4 shrink-0" aria-hidden />
                              <span>Luo tili / Rekisteröidy</span>
                            </span>
                          </NavLink>
                        )}

                        {canShowLogout && (
                          <button
                            type="button"
                            onClick={handleLogout}
                            className="w-full text-left block rounded-lg px-3 py-2 text-sm hover:bg-white/5"
                            role="menuitem"
                          >
                            <span className="inline-flex items-center gap-2">
                              <LogOut className="h-4 w-4 shrink-0" aria-hidden />
                              <span>Kirjaudu ulos</span>
                            </span>
                          </button>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </nav>

              {/* Oikean reunan toiminnot (DESKTOP) */}
              <div className="flex items-center gap-2">

                {/* Pikanhaku */}
                  <div className="hidden lg:block w-32 xl:w-56 2xl:w-64">
                    <SearchBar />
                  </div>

                {/* Teemakytkin */}
                <NeonButton
                  onClick={toggleTheme}
                  variant="ghost"
                  size="md"
                  iconOnly
                  IconLeft={theme === "dark" ? Sun : Spotlight}
                  aria-pressed={theme === "dark"}
                  aria-label={`Vaihda ${theme === "dark" ? "vaaleaan" : "tummaan"} teemaan`}
                  title={`Vaihda ${theme === "dark" ? "vaaleaan" : "tummaan"} teemaan`}
                  className={cx("!rounded-full hidden xxxsm:inline-flex xxsm:inline-flex xsm:inline-flex s24:inline-flex sm:inline-flex md:inline-flex mdd:inline-flex lg:inline-flex xl:inline-flex 2xl:inline-flex", themeToggleBox)}
                />

                {/* Desktop: Ota yhteyttä */}
                {showContactCTA && (
                  <NeonButton
                    onClick={() => navigate("/otayhteytta")}
                    variant="primary"
                    size="sm"
                    className="hidden lg:inline-flex xl:inline-flex 2xl:inline-flex"
                    aria-label="Ota yhteyttä"
                    title="Ota yhteyttä"
                    // IconLeft={Phone}
                  >
                    Ota yhteyttä
                  </NeonButton>
                )}

                {/* Mobile burger */}
                <button
                  onClick={() => setOpen(true)}
                  className={cx(
                    "lg:hidden xl:hidden 2xl:hidden inline-flex h-10 w-10 items-center justify-center rounded-lg border",
                    isDark ? "border-white/20 bg-white/10 text-white hover:bg-white/20"
                           : "border-black/10 bg-black/5 text-black hover:bg-black/10"
                  )}
                  aria-label="Avaa valikko"
                  aria-expanded={open}
                  aria-controls="mobile-menu"
                >
                  <Menu className="h-6 w-6" />
                  <span className="sr-only">Avaa valikko</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </motion.header>

      {/* MOBIILIVALIKKO */}
      <AnimatePresence>
        {open && (
          <>
            {/* Overlay */}
            <motion.div
              key="overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className={cx("fixed inset-0 z-50", overlayClass)}
              onClick={() => setOpen(false)}
              aria-hidden="true"
            />

            {/* Paneeli */}
            <motion.aside
              key="panel"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", stiffness: 260, damping: 30 }}
              className={cx("fixed right-0 top-0 z-50 h-full w-[84%] max-w-sm shadow-2xl overflow-hidden", panelBg)}
              role="dialog"
              aria-label="Päävalikko"
              id="mobile-menu"
            >
              <div className="flex items-center justify-between p-4">
                <span className="font-customFont2 text-2xl">Valikko</span>

                {/* Sulje-nappi */}
                <button
                  onClick={() => setOpen(false)}
                  className={cx(
                    "inline-flex h-10 w-10 items-center justify-center rounded-lg border shadow",
                    "border-white/70 bg-white/20 hover:bg-white/10 text-white"
                  )}
                  aria-label="Sulje valikko"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

                {/* 🔍 Mobiilihaku */}
                  <div className="px-4 pb-3">
                    <SearchBar className="w-full" />
                  </div>

                  <div className="mx-4 h-[2px] rounded-full bg-gradient-to-r from-pink via-electric to-lemon" />


              <div className="mx-4 h-[2px] rounded-full bg-gradient-to-r from-pink via-electric to-lemon" />

              {/* Hehku-tausta */}
              <motion.div
                className="pointer-events-none absolute -top-24 -left-24 h-56 w-56 rounded-full blur-3xl"
                style={{ background: "rgba(236,72,153,0.22)" }}
                animate={{ x: [0, 16, -8, 0], y: [0, -10, 12, 0] }}
                transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
              />
              <motion.div
                className="pointer-events-none absolute -bottom-24 -right-16 h-64 w-64 rounded-full blur-3xl"
                style={{ background: "rgba(59,130,246,0.18)" }}
                animate={{ x: [0, -20, 10, 0], y: [0, 14, -10, 0] }}
                transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
              />

              <div className="px-4 pb-6 relative">
                {/* ✨ Palvelu */}
                <div className="mt-4">
                  <h4 className="px-4 py-2 font-customFont2 font-semibold text-base uppercase tracking-widest text-white/90">
                   Palvelu
                  </h4>
                  <ul className="space-y-2">
                    {navItems.map((item) => (
                      <li key={item.to}>
                        <NavLink
                          to={item.to}
                          onClick={() => setOpen(false)}
                          className={({ isActive }) =>
                            cx(mobileLinkBase, isActive ? mobileLinkActive : mobileLinkInactive)
                          }
                        >
                          <span className="mr-2">{MOBILE_EMOJI[item.to] || "🔗"}</span>
                          {item.label}
                        </NavLink>
                      </li>
                    ))}

                    {/* Vain mobiiliin: Yhteistyö-sivu */}
                    <li>
                      <NavLink
                        to="/yhteistyo"
                        onClick={() => setOpen(false)}
                        className={({ isActive }) =>
                          cx(mobileLinkBase, isActive ? mobileLinkActive : mobileLinkInactive)
                        }
                      >
                        🤝 Yhteistyö
                      </NavLink>
                    </li>
                  </ul>
                </div>

                {/* 🎤 Esiintyjille */}
                <div className="mt-6">
                  <h4 className="px-4 py-2 font-customFont2 font-semibold text-base uppercase tracking-widest text-white/90">
                   Esiintyjille
                  </h4>
                  <ul className="space-y-2">
                    <li>
                      <NavLink
                        to="/ukk-esiintyjille"
                        onClick={() => setOpen(false)}
                        className={({ isActive }) =>
                          cx(mobileLinkBase, isActive ? mobileLinkActive : mobileLinkInactive)
                        }
                      >
                        📖 Esiintyjien UKK
                      </NavLink>
                    </li>

                   {canShowDashboard && (
                      <li>
                        <NavLink
                          to={`/dashboard?${DASH_USER_QS}`}
                          onClick={() => setOpen(false)}
                          className={({ isActive }) =>
                            cx(mobileLinkBase, isActive ? mobileLinkActive : mobileLinkInactive)
                          }
                        >
                          📊 Hallintapaneeli
                        </NavLink>
                      </li>
                    )}

                    {!isAuthed && !isLoginPage && (
                      <li>
                        <NavLink
                          to="/kirjaudu"
                          onClick={() => setOpen(false)}
                          className={({ isActive }) =>
                            cx(mobileLinkBase, isActive ? mobileLinkActive : mobileLinkInactive)
                          }
                        >
                          🔐 Kirjaudu sisään
                        </NavLink>
                      </li>
                    )}

                    {!isAuthed && !isRegisterPage && (
                      <li>
                        <NavLink
                          to="/rekisteroidy"
                          onClick={() => setOpen(false)}
                          className={({ isActive }) =>
                            cx(mobileLinkBase, isActive ? mobileLinkActive : mobileLinkInactive)
                          }
                        >
                          📝 Luo tili / Rekisteröidy
                        </NavLink>
                      </li>
                    )}
                  </ul>
                </div>

                {/* CTA:t + Kirjaudu ulos mobiilissa */}
                <div className="mt-6 space-y-3">
                  {showContactCTA && (
                    <button
                      onClick={() => {
                        setOpen(false);
                        navigate("/otayhteytta");
                      }}
                      className={cx(
                        "inline-flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 font-semibold shadow relative overflow-hidden",
                        isDark ? "bg-electric/80 text-white hover:brightness-110 active:brightness-95"
                               : "bg-pink text-white hover:brightness-110 active:brightness-95"
                      )}
                    >
                      <span
                        className="absolute inset-0 rounded-xl blur-md opacity-70 bg-pink-500/50 dark:bg-primary-500/50"
                        aria-hidden="true"
                      />
                      <Phone className="h-5 w-5 relative z-10" />
                      <span className="relative z-10">Ota yhteyttä</span>
                    </button>
                  )}

                  {canShowLogout && (
                    <button
                      onClick={handleLogout}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 font-semibold shadow bg-white/10 hover:bg-white/20 text-white"
                      aria-label="Kirjaudu ulos"
                      title="Kirjaudu ulos"
                    >
                      <LogOut className="h-5 w-5" />
                      <span>Kirjaudu ulos</span>
                    </button>
                  )}
                </div>

                  {/* Some-linkit – näkyy vain ≥ md (ei mobiilissa) */}
                  <div className="mt-7 hidden md:block">
                    <div className="mx-1 h-px bg-white/10 mb-2" />
                    <p className="px-4 text-xs uppercase tracking-wide text-white/70 mb-2 text-center">
                      Seuraa meitä
                    </p>
                    <SocialLinks
                      variant="mobile"           // vaihda "desktop" jos komponentti käyttää eri layoutia
                      onItemClick={() => setOpen(false)}
                    />
                  </div>

                </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* “Ylös”-nappi kun rullattu alas eikä paneeli auki */}
      {showScrollTop && !open && <DiscoScrollTop bottom={24} right={24} variant="eq" label="Ylös" />}
    </>
  );
}
